import { createContext, ReactElement, useMemo, useState } from 'react';
import { ModalContextType, Props } from './@types';
import Modal from './Modal';

const ModalContext = createContext<ModalContextType>({
  open: false,
  small: false,
  showModal: () => {},
  closeModal: () => {},
  closeAndShow: () => {},
  content: null,
  maxWidth: '',
  padding: ''
});

const ModalProvider = ({ children }: Props) => {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState<ReactElement | ReactElement[] | null>(
    null
  );
  const [maxWidth, setMaxWidth] = useState('');
  const [small, setSmall] = useState(false);
  const [padding, setPadding] = useState('');

  const showModal = (
    _content: ReactElement | ReactElement[] | null,
    _maxWidth: string,
    _small: boolean,
    _padding?: string
  ) => {
    setContent(_content);
    setMaxWidth(_maxWidth);
    setSmall(_small);
    setPadding(_padding ?? '');
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setContent(content);
  };

  const closeAndShow = (
    _content: ReactElement | ReactElement[] | null,
    _maxWidth: string,
    _small: boolean,
    _padding?: string
  ): void => {
    closeModal();

    // Smoth animation
    const FIVE_HUNDRED_FIFTY_MILLISECONDS = 550;
    const timeout = setTimeout(() => {
      showModal(_content, _maxWidth, _small, _padding);
      clearTimeout(timeout);
    }, FIVE_HUNDRED_FIFTY_MILLISECONDS);
  };

  const value = useMemo(
    () => ({
      open,
      content,
      maxWidth,
      small,
      padding,
      showModal,
      closeModal,
      closeAndShow
    }),
    [
      open,
      content,
      maxWidth,
      small,
      padding,
      showModal,
      closeModal,
      closeAndShow
    ]
  );

  return (
    <ModalContext.Provider value={value}>
      <Modal />
      {children}
    </ModalContext.Provider>
  );
};

export { ModalProvider, ModalContext };
